'use client';

import { Link, useRouter } from '@/i18n/navigation';
import {
  Box,
  Button,
  Center,
  Container,
  HStack,
  StackSeparator,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useSearchParams } from 'next/navigation';
import OrderCard from './order-card';

import { useAuthStore } from '@/app/store/use-auth-store';
import { useOrdersList } from '@/entities/order/api/client';
import { ISingleOrder } from '@/entities/order/model/types';
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from '@/features/pagination';
import BottomNavigation from '@/widgets/bottom-navigation/ui/bottom-navigation';
import UniversalHeader from '@/widgets/header';
import { useTranslations } from 'next-intl';
import Loading from './loading';

function OrdersList() {
  // const { user } = useSelector((state: IRedux) => state.auth);
  const searchParams = useSearchParams();
  const router = useRouter();
  const page = searchParams.get('page');
  const t = useTranslations();
  const { user } = useAuthStore();

  const { data: ordersData, isLoading } = useOrdersList(user?.id || '', {
    page: 1,
    limit: 20,
  });

  if (Number(ordersData?.count) === 0)
    return (
      <>
        <UniversalHeader isBackIcon={false} title={t('orders')} />
        <Center h={'80vh'} flexDir={'column'}>
          <Text fontSize={20} fontWeight={600}>
            {t('you_have_no_orders_yet')}
          </Text>
          <Link href="/">
            <Button
              bg="primary"
              color="primary.text"
              _hover={{ bg: 'primary.hover' }}
              size={'xl'}
              rounded={12}
              mt={4}
            >
              {t('go_to_menu')}
            </Button>
          </Link>
        </Center>
        <BottomNavigation />
      </>
    );

  if (isLoading) return <Loading />;

  return (
    <>
      <VStack
        display="flex"
        separator={<StackSeparator borderColor={'background'} />}
        mb={'90px'}
      >
        <Box w="100%">
          <UniversalHeader
            isBackIcon={false}
            title={t('orders')}
            icon={
              <Text fontSize="sm" color="gray.600">
                {t('total_count')}:&nbsp;{ordersData?.count}
              </Text>
            }
          />
        </Box>

        {ordersData?.orders?.map((order: ISingleOrder) => (
          <Container key={order.id}>
            <Link href={`/orders/${order.id}`}>
              <OrderCard order={order} />
            </Link>
          </Container>
        ))}

        {Number(ordersData?.count) > 20 && (
          <PaginationRoot
            count={Number(ordersData?.count)}
            pageSize={20}
            defaultPage={1}
            page={Number(page) || 1}
            variant="subtle"
            mt={4}
            onPageChange={(e) => router.push(`?page=${e.page}`)}
          >
            <HStack>
              <PaginationPrevTrigger color={'text.muted'} />
              <PaginationItems color="text" />
              <PaginationNextTrigger color={'text.muted'} />
            </HStack>
          </PaginationRoot>
        )}
      </VStack>
      <BottomNavigation />
    </>
  );
}

export default OrdersList;
